import { ExtractableSection } from './useAutofillFromResume';

export type AIInput = {
  prompt: string;
  systemMessage?: string;
};

export const DEFAULT_SYSTEM_MESSAGE = `You are an expert technical recruiter with extensive experience in software development roles. Your objective is to meticulously analyze developer resumes to extract comprehensive and pertinent information.

    Guidelines:
    1. Identify and catalog all technical skills, frameworks, and programming languages mentioned in the resume.
    2. Detect patterns in the types of projects and industries the candidate has experience in.
    3. Assess both current and past roles to evaluate the candidate's expertise and professional growth.
    4. Utilize precise technical terminology to ensure clarity and avoid ambiguity.
    5. Maintain a professional, concise, and objective tone throughout the analysis.
    6. Highlight key achievements, their technical impact, and any notable projects.
    7. Identify any leadership roles, mentoring experiences, or instances of team collaboration.
    8. If specific information is unclear or missing from the resume, denote it with 'not provided'.
    9. Ensure that the extracted information is accurate, detailed, and up-to-date.

    Ensure that extracted summaries accurately reflect the candidate's overall experience and technical proficiency.
    `;

const summaryGenetationPrompt = {
  prompt: `Analyze the developer's resume and generate a professional summary that includes:
        1. Total years of relevant development experience across all positions;
        2. Primary technical expertise and core technology stack;
        3. Notable projects or technical achievements;
        4. Industry experience and domain knowledge;
        5. Leadership roles, mentoring experiences, or team collaboration highlights;
        6. Key soft skills demonstrated through their experience;
        7. Any other relevant information that can help the hiring manager make an informed decision;
        8. Format the summary as a concise, well-structured paragraph that flows naturally with a maximum of 150 words;
        9. Use only the first name of the candidate when creating the summary.

        Format the summary as a concise, well-structured paragraph that flows naturally.`,
};

const skillsGenerationPrompt = {
  prompt: `Extract all technical skills from the resume, categorizing them with the following fields:
    - name
    - experience (in years) - if not provided, use one of the following: 'Less than 1 year', '1-2 years', '2-3 years', '3-4 years', '4-5 years', '5-6 years', '6-7 years', '7-8 years', '8-9 years', '9-10 years', 'Above 10 years'
    - description
    - related technologies
    - isMainStack (boolean) - set to true if the skill is the developer's main stack

    Format the output as a list of objects.`,
};

const basicInfoGenerationPrompt = {
  prompt: `Extract essential contact and professional information from the resume:
    1. First Name
    2. Last Name
    3. Email Address
    4. Mobile Number
    5. Country
    6. City
    7. Developer Type (e.g., Frontend, Backend, Fullstack)
    8. Primary Tech Stack
    9. Total Years of Experience
    10. English Proficiency Level
    11. Expected Salary (in USD)
    12. GitHub URL
    13. LinkedIn URL

    Present the extracted information as a clear list of key-value pairs.`,
};

const experienceGenerationPrompt = {
  prompt: `Extract key professional information from the resume and return it as a JSON array of objects with the following structure:
    [
      {
        "title": string,
        "company": string,
        "description": string,
        "endMonth": string, // Use "---" if currently employed, otherwise use the full month name
        "endYear": string, // Use "---" if currently employed, otherwise use the full year
        "isCurrentWork": boolean,
        "location": string,
        "startMonth": string, // Use the full month name
        "startYear": string,
        "techs": string[] // Array of technology names
      }
    ]

    Requirements:
    - Extract 3-6 bullet points highlighting key responsibilities and achievements
    - Each bullet point should start with a strong action verb
    - Include quantifiable results when available (e.g., "Reduced deployment time by 40%")
    - Mention specific technologies, frameworks, or methodologies used
    - Focus on impact and outcomes, not just tasks

    Format each bullet point as:
    • [Action verb] [what was done] [impact/result if available] [technologies used if applicable]

    Order the array chronologically from oldest to most recent position. Use startMonth and startYear to determine the order.
    If description is not available, use "Not provided".`,
  systemMessage: `You are an expert resume parser specializing in extracting professional work experience. 
    Your task is to convert work experience descriptions into concise, impactful bullet points 
    following these guidelines:

    1. Use bullet points (•) for each distinct responsibility or achievement
    2. Start each bullet with a strong action verb (e.g., Developed, Led, Implemented, Designed)
    3. Focus on measurable achievements and impact when available
    4. Keep each bullet point concise (1-2 lines maximum)
    5. Include technical skills and technologies used
    6. Prioritize recent and relevant accomplishments
    7. Maintain professional tone and avoid first-person pronouns`,
};

const educationGenerationPrompt = {
  prompt: `Extract key education details as a JSON array of objects with the following structure:
    [
      {
        "currentlyPursuing": boolean // true if currently in a degree program
        "degree": string,
        "endMonth": string, // use empty string if currently pursuing, use the full month name and not the year
        "endYear": string, // use empty string if currently pursuing
        "startMonth": string // use the full month name and not the year
        "startYear": string,
        "university": string,
      }
    ]

  List in chronological order, from oldest to most recent.`,
};

const projectsGenerationPrompt = {
  prompt: `Extracts the personal projects from the resume. The data should be in the following format:
        [
          {
            "description": string,
            "link": string,
            "techs": string[] // array of technology names
            "title": string,
            "year": string // use empty string if not available
          }
        ]
        List in chronological order, from oldest to most recent.
        If there's no projects, the data should be a empty array.`,
};

export const promptsPerSection: Record<ExtractableSection, AIInput> = {
  summary: summaryGenetationPrompt,
  skills: skillsGenerationPrompt,
  basicInfo: basicInfoGenerationPrompt,
  experience: experienceGenerationPrompt,
  education: educationGenerationPrompt,
  projects: projectsGenerationPrompt,
};
